import { withMutations, withResource } from '@angular-architects/ngrx-toolkit';
import { computed, effect, inject, linkedSignal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { createRxMutation, createRxResource, NotificationActions } from '@next/core-lib/notification';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { ActionsSubject, Store } from '@ngrx/store';
import { filter, forkJoin, map, of, take } from 'rxjs';

import { BemApiService } from '../../bem/shared/services/bem-api.service';
import { IrrigationDialogComponent } from '../../irrigations/components/irrigation-dialog/irrigation-dialog.component';
import { IrrigationDialogData } from '../../irrigations/models/ui-models';
import { CropFieldAdvice } from '../../shared/components/disease-overview-table.component/models/models';
import { SprayApplicationDialogService } from '../../shared/spray-applications/services/spray-application-dialog.service';
import { AppState, SprayTypes } from '../../shared/models';
import { DateService } from '../../shared/services/date.service';
import { withCustomerParams } from '../../shared/utils/customer-params.utils';
import { dispatchValidationErrorSnackbars } from '../../shared/utils/error-handling.utils';
import { mapCropFields } from '../models/api-mappers';
import {
  BlightChartDetail,
  BlightCropFieldAdvice,
  BlightData,
  CropFieldRegistration,
  Day,
  ParticipatingCropField,
  PhytophthoraPressure,
} from '../../blight-v2/models/models';
import { BlightService } from '../../blight-v2/services/blight.service';
import { BlightNotificationService } from '../services/blight-notification.service';
import { defaultBlightDayRange } from '../../blight-v2/shared/helpers/blight-helpers';
import { BlightCustomerContextService } from '../services/blight-customer-context.service';
import { isBlightSyncStatusRecalculating } from './blight-sync-status';
import { cropYearChanged } from '../../store/actions';
import { isCropYearReadOnly, selectCropYear } from '../../store/selectors';

interface BlightResourceParams {
  customerId: number;
  cropYear: number;
  fromDate: Date;
  toDate: Date;
}

interface SelectedCropFieldParams extends BlightResourceParams {
  cropFieldId: number;
  cropYear: number;
}

interface SelectedCropFieldRegistrationParams {
  customerId: number;
  cropFieldId: number;
  cropYear: number;
}

interface BlightRecalculationRequest {
  cropFieldId: number;
  year: number;
}

interface CropFieldDateUpdateRequest {
  cropFieldId: number;
  year: number;
  date: Date;
}

interface CropFieldPressureUpdateRequest {
  cropFieldId: number;
  year: number;
  phytophthoraPressure: PhytophthoraPressure;
}

interface CropFieldSuppressUpdateRequest {
  cropFieldId: number;
  year: number;
  suppressSprayAdvice: boolean;
}

interface CropFieldVisibilityUpdateRequest {
  cropFieldId: number;
  year: number;
  isVisible: boolean;
}

interface BlightState {
  selectedDates: Date[];
  fromDate?: Date;
  toDate?: Date;
  selectedCropFieldId: number | undefined;
  hasBlightPremium: boolean;
  recalculatingCropFieldIds: number[];
}

const initialState: BlightState = {
  selectedDates: [],
  fromDate: undefined,
  toDate: undefined,
  selectedCropFieldId: undefined,
  hasBlightPremium: false,
  recalculatingCropFieldIds: [],
};

export const BlightSignalStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(() => ({
    cropYear: inject(Store<AppState>).selectSignal(selectCropYear),
  })),
  withResource(
    (store) => {
      const blightService = inject(BlightService);
      const bemApiService = inject(BemApiService);
      const customerContext = inject(BlightCustomerContextService);
      const customer = customerContext.customer;

      const customerBlightRights = createRxResource<
        { isPremium: boolean; customerId: number },
        { customerId: number; cropYear: number } | undefined
      >({
        params: () =>
          withCustomerParams(customer()?.id, (customerId) => ({
            customerId,
            cropYear: store.cropYear(),
          })),
        stream: ({ params }) => blightService.getCustomerBlightRights(params.customerId, params.cropYear),
        errorMessage: 'errors.blight.genericError',
      });

      const blightData = createRxResource<BlightData[], BlightResourceParams | undefined>({
        params: () =>
          withCustomerParams(customer()?.id, (customerId) => {
            if (!store.hasBlightPremium() || !store.fromDate() || !store.toDate()) {
              return undefined;
            }

            return {
              customerId,
              cropYear: store.cropYear(),
              fromDate: store.fromDate(),
              toDate: store.toDate(),
            };
          }),
        stream: ({ params }) =>
          blightService
            .getBlightData(params.fromDate, params.toDate, params.customerId, params.cropYear)
            .pipe(map((newData) => mergeBlightData(blightData.value(), newData))),
        defaultValue: [],
        errorMessage: 'errors.blight.genericError',
      });

      const participatingCropFields = createRxResource<
        ParticipatingCropField[],
        { customerId: number; cropYear: number } | undefined
      >({
        params: () =>
          withCustomerParams(customer()?.id, (customerId) => ({
            customerId,
            cropYear: store.cropYear(),
          })),
        stream: ({ params }) => blightService.getParticipatingCropFields(params.cropYear, params.customerId),
        defaultValue: [],
        errorMessage: 'errors.blight.genericError',
      });

      const selectedCropFieldDetails = createRxResource<BlightChartDetail[], SelectedCropFieldParams | undefined>({
        params: () =>
          withCustomerParams(customer()?.id, (customerId) => {
            const cropFieldId = store.selectedCropFieldId();
            const fromDate = store.fromDate();
            const toDate = store.toDate();

            if (!cropFieldId || !fromDate || !toDate || !store.hasBlightPremium()) {
              return undefined;
            }

            return {
              customerId,
              cropFieldId,
              cropYear: store.cropYear(),
              fromDate,
              toDate,
            };
          }),
        stream: ({ params }) =>
          blightService.getBlightChartDetailsByCropField(
            params.cropYear,
            params.cropFieldId,
            formatDate(params.fromDate),
            formatDate(params.toDate),
            params.customerId,
          ),
        defaultValue: [],
        errorMessage: 'errors.blight.genericError',
      });

      const selectedCropFieldRegistration = createRxResource<
        CropFieldRegistration | undefined,
        SelectedCropFieldRegistrationParams | undefined
      >({
        params: () =>
          withCustomerParams(customer()?.id, (customerId) => {
            const cropFieldId = store.selectedCropFieldId();
            if (!cropFieldId || !store.hasBlightPremium()) {
              return undefined;
            }

            return {
              customerId,
              cropFieldId,
              cropYear: store.cropYear(),
            };
          }),
        stream: ({ params }) =>
          blightService.getCropFieldRegistration(params.cropFieldId, params.cropYear, params.customerId),
        defaultValue: undefined,
        errorMessage: 'errors.blight.genericError',
      });

      const selectedCropFieldBemParticipation = createRxResource<{ isParticipating: boolean }, number | undefined>({
        params: () => store.selectedCropFieldId(),
        stream: ({ params }) => bemApiService.getIsBemCropField(params),
        defaultValue: { isParticipating: false },
        errorMessage: 'errors.blight.genericError',
      });

      return {
        customerBlightRights,
        blightData,
        participatingCropFields,
        selectedCropFieldDetails,
        selectedCropFieldRegistration,
        selectedCropFieldBemParticipation,
      };
    },
    {
      errorHandling: 'previous value',
    },
  ),
  withMutations((store) => {
    const blightService = inject(BlightService);
    const customerContext = inject(BlightCustomerContextService);
    const appStore = inject(Store<AppState>);
    const localeService = inject(LocaleService);

    const reloadSelectedCropFieldData = (): void => {
      store._blightDataReload();
      store._selectedCropFieldDetailsReload();
      store._selectedCropFieldRegistrationReload();
    };

    const reloadBlightAdviceData = (): void => {
      store._blightDataReload();
      store._selectedCropFieldDetailsReload();
    };

    const setRecalculatingCropFieldIds = (cropFieldIds: number[]): void => {
      const uniqueIds = Array.from(new Set([...untracked(() => store.recalculatingCropFieldIds()), ...cropFieldIds]));
      patchState(store, { recalculatingCropFieldIds: uniqueIds });
    };

    const removeRecalculatingCropFieldIds = (cropFieldIds: number[]): void => {
      if (!cropFieldIds.length) {
        return;
      }

      patchState(store, {
        recalculatingCropFieldIds: untracked(() =>
          store.recalculatingCropFieldIds().filter((cropFieldId) => !cropFieldIds.includes(cropFieldId)),
        ),
      });
    };

    const applyCropFieldRegistrationUpdate = (registration: CropFieldRegistration): void => {
      patchState(store, {
        selectedCropFieldRegistrationValue:
          untracked(() => store.selectedCropFieldId()) === registration.cropFieldId
            ? registration
            : untracked(() => store.selectedCropFieldRegistrationValue()),
        blightDataValue: untracked(() =>
          store.blightDataValue().map((data) =>
            data.cropFieldId === registration.cropFieldId
              ? {
                ...data,
                plantDate: registration.plantDate,
                emergenceDate: registration.emergenceDate,
                harvestDate: registration.harvestDate,
              }
              : data,
          ),
        ),
      });
    };

    const restoreSelectedCropFieldRegistration = (): void => {
      const currentRegistration = untracked(() => store.selectedCropFieldRegistrationValue());
      if (!currentRegistration) {
        return;
      }

      patchState(store, {
        selectedCropFieldRegistrationValue: {
          ...currentRegistration,
        },
      });
    };

    return {
      recalculateBlight: createRxMutation<BlightRecalculationRequest[], unknown[]>({
        operation: (requests) =>
          requests.length
            ? forkJoin(
              requests.map(({ cropFieldId, year }) =>
                blightService.recalculateBlightData(cropFieldId, year, customerContext.customer()?.id),
              ),
            )
            : of([]),
        onSuccess: (_, request) => {
          setRecalculatingCropFieldIds(request.map(({ cropFieldId }) => cropFieldId));
        },
        onError: (_, request) => {
          removeRecalculatingCropFieldIds(request.map(({ cropFieldId }) => cropFieldId));
        },
        notification: { errorMessage: 'errors.blight.failedToRecalculate' },
      }),
      updatePlantDate: createRxMutation<CropFieldDateUpdateRequest, CropFieldRegistration>({
        operation: ({ cropFieldId, date }) =>
          blightService.updatePlantDate(cropFieldId, date, customerContext.customer()?.id),
        onSuccess: (registration) => {
          applyCropFieldRegistrationUpdate(registration);
          reloadBlightAdviceData();
          appStore.dispatch(
            NotificationActions.infoSnackbar({
              message: localeService.translate('BFP.Notifications.PlantDateUpdated', {
                plantDate:
                  registration.plantDate?.toLocaleDateString(localeService.getLocale()) ?? 'Failed to parse plant date',
              }),
            }),
          );
        },
        onError: (error) => {
          dispatchValidationErrorSnackbars(error, appStore, localeService);
          restoreSelectedCropFieldRegistration();
        },
        notification: { errorMessage: 'errors.blight.genericError' },
      }),
      updateEmergenceDate: createRxMutation<CropFieldDateUpdateRequest, CropFieldRegistration>({
        operation: ({ cropFieldId, date }) =>
          blightService.updateEmergenceDate(cropFieldId, date, customerContext.customer()?.id),
        onSuccess: (registration) => {
          applyCropFieldRegistrationUpdate(registration);
          reloadBlightAdviceData();
        },
        onError: (error) => {
          dispatchValidationErrorSnackbars(error, appStore, localeService);
          restoreSelectedCropFieldRegistration();
        },
        notification: { errorMessage: 'errors.blight.genericError' },
      }),
      updateHarvestDate: createRxMutation<CropFieldDateUpdateRequest, CropFieldRegistration>({
        operation: ({ cropFieldId, date }) =>
          blightService.updateHarvestDate(cropFieldId, date, customerContext.customer()?.id),
        onSuccess: (registration) => {
          applyCropFieldRegistrationUpdate(registration);
          reloadBlightAdviceData();
        },
        onError: (error) => {
          dispatchValidationErrorSnackbars(error, appStore, localeService);
          restoreSelectedCropFieldRegistration();
        },
        notification: { errorMessage: 'errors.blight.genericError' },
      }),
      updatePhytophthoraPressure: createRxMutation<CropFieldPressureUpdateRequest, CropFieldRegistration>({
        operation: ({ cropFieldId, year, phytophthoraPressure }) =>
          blightService.updatePhytophthoraPressure(
            cropFieldId,
            year,
            phytophthoraPressure,
            customerContext.customer()?.id,
          ),
        onSuccess: () => {
          reloadSelectedCropFieldData();
        },
        notification: { errorMessage: 'errors.blight.genericError' },
      }),
      updateSuppressSprayAdvice: createRxMutation<CropFieldSuppressUpdateRequest, CropFieldRegistration>({
        operation: ({ cropFieldId, year, suppressSprayAdvice }) =>
          blightService.updateSuppressSprayAdvice(
            cropFieldId,
            year,
            suppressSprayAdvice,
            customerContext.customer()?.id,
          ),
        onSuccess: () => {
          reloadSelectedCropFieldData();
        },
        notification: { errorMessage: 'errors.blight.genericError' },
      }),
      updateCropFieldVisibility: createRxMutation<CropFieldVisibilityUpdateRequest, CropFieldRegistration>({
        operation: ({ cropFieldId, year, isVisible }) =>
          blightService.updateCropFieldVisibilityStatus(cropFieldId, year, isVisible, customerContext.customer()?.id),
        onSuccess: (_, request) => {
          patchState(store, {
            blightDataValue: untracked(() =>
              store
                .blightDataValue()
                .map((data) =>
                  data.cropFieldId === request.cropFieldId ? { ...data, isCropFieldVisible: request.isVisible } : data,
                ),
            ),
          });

          if (!request.isVisible && store.selectedCropFieldId() === request.cropFieldId) {
            patchState(store, { selectedCropFieldId: undefined });
          }
        },
        notification: { errorMessage: 'errors.blight.genericError' },
      }),
    };
  }),
  withComputed((store, dateService = inject(DateService), localeService = inject(LocaleService)) => {
    const allBlightAdvices = computed((): BlightCropFieldAdvice[] => {
      const cropFieldsById = new Map<number, ParticipatingCropField>();
      store.participatingCropFieldsValue().forEach((cropField) => {
        cropFieldsById.set(cropField.id, cropField);
      });

      return store
        .blightDataValue()
        .map((data) => {
          const cropField = cropFieldsById.get(data.cropFieldId);
          return cropField ? mapCropFields(data, cropField) : null;
        })
        .filter((advice): advice is BlightCropFieldAdvice => advice !== null)
        .sort((a, b) => a.cropFieldName.localeCompare(b.cropFieldName));
    });

    const visibleBlightAdvices = computed(() => allBlightAdvices().filter((advice) => advice.isCropFieldVisible));

    const eligibleRecalculationRequests = computed((): BlightRecalculationRequest[] =>
      visibleBlightAdvices()
        .filter((cropField) => isCropFieldEligibleForRecalculation(cropField, dateService))
        .map((cropField) => ({ cropFieldId: cropField.cropFieldId, year: cropField.cropYear })),
    );

    return {
      pageTitle: computed(() => localeService.translate('blightV2.advice.title')),
      blightAdvices: computed(() => allBlightAdvices()),
      visibleBlightAdvices,
      diseaseCropFields: computed(() =>
        allBlightAdvices().map((advice) => ({
          id: advice.cropFieldId,
          name: advice.cropFieldName,
          cropVarietyName: advice.cropVarietyName,
          areaInHectares: advice.amountOfHectares,
          isVisible: advice.isCropFieldVisible,
        })),
      ),
      blightCalculationDate: linkedSignal<BlightCropFieldAdvice[], Date | null>({
        source: () => visibleBlightAdvices(),
        computation: (advices, previous): Date | null => {
          const newValue = advices.reduce(
            (minDate, data) => {
              const calculatedOn =
                store.blightDataValue().find((blightData) => blightData.cropFieldId === data.cropFieldId)?.blightResult
                  ?.calculatedOn ?? null;

              return calculatedOn && (!minDate || calculatedOn < minDate) ? calculatedOn : minDate;
            },
            null as Date | null,
          );
          return newValue ?? previous?.value ?? null;
        },
      }),
      blightMinAdviceDate: linkedSignal<BlightCropFieldAdvice[], Date | undefined>({
        source: () => visibleBlightAdvices(),
        computation: (advices, previous): Date | undefined => {
          let minDate: Date | undefined;

          advices.forEach((cropField) => {
            if (cropField.plantDate && (!minDate || cropField.plantDate < minDate)) {
              minDate = cropField.plantDate;
            }
          });

          return minDate ?? previous?.value;
        },
      }),
      blightMaxAdviceDate: computed(() =>
        dateService.daysInTheFuture(defaultBlightDayRange.daysInFuture, store.cropYear()),
      ),
      eligibleBlightRecalculationRequests: computed(() => eligibleRecalculationRequests()),
      eligibleBlightCropFieldIds: computed(() => eligibleRecalculationRequests().map((request) => request.cropFieldId)),
      hasEligibleBlightCropFields: computed(() => eligibleRecalculationRequests().length > 0),
      isBlightCropFieldsSyncing: computed(
        () =>
          store.recalculateBlightIsPending() ||
          store.recalculatingCropFieldIds().length > 0 ||
          store.blightDataValue().some((data) => isBlightSyncStatusRecalculating(data.syncStatus)),
      ),
      selectedCropFieldAdvice: computed(() =>
        visibleBlightAdvices().find((advice) => advice.cropFieldId === store.selectedCropFieldId()),
      ),
      hasSelectedCropFieldAdvices: computed(() => {
        const advice = visibleBlightAdvices().find((item) => item.cropFieldId === store.selectedCropFieldId());
        return (advice?.advices?.length ?? 0) > 0;
      }),
      selectedCropFieldChartDetails: computed(() => store.selectedCropFieldDetailsValue()),
      selectedCropFieldRegistrationModel: computed(() => store.selectedCropFieldRegistrationValue()),
      selectedCropFieldIsParticipatingToBem: computed(
        (): boolean => store.selectedCropFieldBemParticipationValue().isParticipating,
      ),
      isSelectedCropFieldIneligibleForRecalculation: computed(() => {
        const registration = store.selectedCropFieldRegistrationValue();
        if (!registration) {
          return false;
        }

        const plantDateInFuture = registration.plantDate
          ? dateService.isInTheFuture(new Date(registration.plantDate))
          : false;
        const harvestDateInPast = registration.harvestDate
          ? dateService.isInThePast(new Date(registration.harvestDate))
          : false;
        return plantDateInFuture || harvestDateInPast;
      }),
    };
  }),
  withMethods(
    (
      store,
      dateService = inject(DateService),
      localeService = inject(LocaleService),
      dialogService = inject(DialogService),
      appStore = inject(Store<AppState>),
      sprayApplicationDialogService = inject(SprayApplicationDialogService),
    ) => {
      const addRecalculatingCropFieldIds = (cropFieldIds: number[]): void => {
        const uniqueIds = Array.from(new Set([...untracked(() => store.recalculatingCropFieldIds()), ...cropFieldIds]));
        patchState(store, { recalculatingCropFieldIds: uniqueIds });
      };

      const requestBlightRecalculation = (requests: BlightRecalculationRequest[]): void => {
        const uniqueRequests = Array.from(
          new Map(
            requests
              .filter((request) => !isCropYearReadOnly(request.year, dateService.currentYear()))
              .map((request) => [`${request.cropFieldId}-${request.year}`, request]),
          ).values(),
        );

        if (!uniqueRequests.length) {
          return;
        }

        addRecalculatingCropFieldIds(uniqueRequests.map(({ cropFieldId }) => cropFieldId));
        store.recalculateBlight(uniqueRequests);
      };

      return {
        requestBlightRecalculation(requests: BlightRecalculationRequest[]): void {
          requestBlightRecalculation(requests);
        },
        patchSelectedDates(dates: Date[]): void {
          const fromDate = dates[0];
          const toDate = dates.at(-1);

          if (!fromDate || !toDate) {
            return;
          }

          patchState(store, { selectedDates: dates, fromDate, toDate });
        },
        resetBlightCropFields(selectedCropFieldId?: number): void {
          const dates = dateService.generateDateRange(
            defaultBlightDayRange.daysInPast,
            defaultBlightDayRange.daysInFuture,
            store.cropYear(),
          );

          patchState(store, {
            selectedDates: dates,
            fromDate: dates[0],
            toDate: dates.at(-1),
            selectedCropFieldId,
          });
        },
        resetForCropYear(cropYear: number): void {
          const dates = dateService.generateDateRange(
            defaultBlightDayRange.daysInPast,
            defaultBlightDayRange.daysInFuture,
            cropYear,
          );

          patchState(store, {
            selectedDates: dates,
            fromDate: dates[0],
            toDate: dates.at(-1),
            selectedCropFieldId: undefined,
            blightDataValue: [],
            participatingCropFieldsValue: [],
            selectedCropFieldDetailsValue: [],
            selectedCropFieldRegistrationValue: undefined,
            selectedCropFieldBemParticipationValue: { isParticipating: false },
            recalculatingCropFieldIds: [],
          });
        },
        selectCropField(cropFieldId: number): void {
          patchState(store, { selectedCropFieldId: cropFieldId });
        },
        clearSelectedCropField(): void {
          patchState(store, { selectedCropFieldId: undefined });
        },
        setRecalculatingCropFieldIds(cropFieldIds: number[]): void {
          const uniqueIds = Array.from(
            new Set([...untracked(() => store.recalculatingCropFieldIds()), ...cropFieldIds]),
          );
          patchState(store, { recalculatingCropFieldIds: uniqueIds });
        },
        removeRecalculatingCropFieldId(cropFieldId: number): void {
          patchState(store, {
            recalculatingCropFieldIds: untracked(() => store.recalculatingCropFieldIds()).filter(
              (id) => id !== cropFieldId,
            ),
          });
        },
        completeCalculation(cropFieldId: number): void {
          patchState(store, {
            recalculatingCropFieldIds: untracked(() => store.recalculatingCropFieldIds()).filter(
              (id) => id !== cropFieldId,
            ),
          });
          store._blightDataReload();
          store._selectedCropFieldDetailsReload();
          store._selectedCropFieldRegistrationReload();
        },
        notifyCalculationFailed(cropFieldId: number): void {
          patchState(store, {
            recalculatingCropFieldIds: untracked(() => store.recalculatingCropFieldIds()).filter(
              (id) => id !== cropFieldId,
            ),
          });
          appStore.dispatch(
            NotificationActions.infoSnackbar({
              message: localeService.translate('blight.syncStatusErrorMessage'),
            }),
          );
        },
        handleCropFieldRegistrationUpdated(_: CropFieldRegistration): void {
          store._blightDataReload();
          store._selectedCropFieldDetailsReload();
          store._selectedCropFieldRegistrationReload();
        },
        openSpraysDialog(advices: CropFieldAdvice[], actionDate: Date | undefined): void {
          sprayApplicationDialogService.openFromCropFields('blight', advices, SprayTypes.PreventiveSpray, actionDate);
        },
        openBlightIrrigation(advices: CropFieldAdvice[], actionDate?: Date): void {
          const cropYear = advices[0]?.cropYear;
          const dialogData: IrrigationDialogData = {
            title: localeService.translate('irrigation.dialogTitle'),
            actionDate: actionDate ?? dateService.now(),
            readOnly: cropYear != null && isCropYearReadOnly(cropYear, dateService.currentYear()),
            selectedCropFields: advices.map((advice) => ({
              id: advice.cropFieldId,
              name: advice.cropFieldName,
              areaInHectare: advice.amountOfHectares,
            })),
          };

          dialogService
            .fullscreenDialog(IrrigationDialogComponent, dialogData)
            .pipe(take(1))
            .subscribe((dialogResult) => {
              const modifiedCropFieldIds = getModifiedCropFieldIds(dialogResult.result);
              const requests = advices
                .filter((advice) => modifiedCropFieldIds.includes(advice.cropFieldId))
                .map((advice) => ({ cropFieldId: advice.cropFieldId, year: advice.cropYear }));

              requestBlightRecalculation(requests);
            });
        },
      };
    },
  ),
  withHooks({
    onInit(store): void {
      const customerContext = inject(BlightCustomerContextService);
      const notificationService = inject(BlightNotificationService);
      const actions = inject(ActionsSubject);

      store.resetBlightCropFields();

      customerContext.customerSwitches$
        .pipe(
          filter(({ previousCustomerId }) => previousCustomerId !== null),
          takeUntilDestroyed(),
        )
        .subscribe(() => {
          patchState(store, {
            hasBlightPremium: false,
            blightDataValue: [],
            participatingCropFieldsValue: [],
            selectedCropFieldDetailsValue: [],
            selectedCropFieldRegistrationValue: undefined,
            selectedCropFieldBemParticipationValue: { isParticipating: false },
            selectedCropFieldId: undefined,
            recalculatingCropFieldIds: [],
          });
          store.resetBlightCropFields();
          store._customerBlightRightsReload();
          store._participatingCropFieldsReload();
          store._blightDataReload();
        });

      effect(() => {
        patchState(store, {
          hasBlightPremium: store.customerBlightRightsValue()?.isPremium ?? false,
        });
      });

      notificationService.onInitiated.pipe(takeUntilDestroyed()).subscribe((event) => {
        if (customerContext.customer()?.id !== event.CustomerId) {
          return;
        }

        store.setRecalculatingCropFieldIds([event.CropFieldId]);
      });

      notificationService.onCompleted.pipe(takeUntilDestroyed()).subscribe((event) => {
        if (customerContext.customer()?.id !== event.CustomerId) {
          return;
        }

        store.completeCalculation(event.CropFieldId);
      });

      notificationService.onFailed.pipe(takeUntilDestroyed()).subscribe((event) => {
        if (customerContext.customer()?.id !== event.CustomerId) {
          return;
        }

        store.notifyCalculationFailed(event.CropFieldId);
      });

      actions
        .pipe(
          filter((action) => action.type === cropYearChanged.type),
          map((action) => action as ReturnType<typeof cropYearChanged>),
          takeUntilDestroyed(),
        )
        .subscribe(({ cropYear }) => store.resetForCropYear(cropYear));
    },
  }),
);

const formatDate = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

const getModifiedCropFieldIds = (result: unknown): number[] => {
  if (
    typeof result === 'object' &&
    result !== null &&
    'modifiedCropFieldIds' in result &&
    Array.isArray(result.modifiedCropFieldIds)
  ) {
    return result.modifiedCropFieldIds;
  }

  return [];
};

const mergeBlightData = (oldData: BlightData[], newData: BlightData[]): BlightData[] => {
  const mergedData = new Map<number, BlightData>();

  oldData.forEach((oldBlightData) => {
    mergedData.set(oldBlightData.cropFieldId, oldBlightData);
  });

  newData.forEach((newBlightData) => {
    const oldBlightData = mergedData.get(newBlightData.cropFieldId);

    if (oldBlightData) {
      mergedData.set(newBlightData.cropFieldId, {
        ...newBlightData,
        blightResult: {
          ...newBlightData.blightResult,
          days: oldBlightData.blightResult
            ? mergeBlightDays(oldBlightData.blightResult.days ?? [], newBlightData.blightResult?.days ?? [])
            : [],
        },
      });
    } else {
      mergedData.set(newBlightData.cropFieldId, newBlightData);
    }
  });

  return Array.from(mergedData.values());
};

const mergeBlightDays = (oldDays: Day[], newDays: Day[]): Day[] => {
  const mergedDays = new Map<number, Day>();

  oldDays.forEach((day) => {
    mergedDays.set(getDayKey(day.date), day);
  });

  newDays.forEach((day) => {
    mergedDays.set(getDayKey(day.date), day);
  });

  return Array.from(mergedDays.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
};

const getDayKey = (date: Date): number => {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value.getTime();
};

const isCropFieldEligibleForRecalculation = (
  cropField: Pick<BlightCropFieldAdvice, 'plantDate' | 'harvestDate'>,
  dateService: DateService,
): boolean => {
  const plantDateInFuture = cropField.plantDate ? dateService.isInTheFuture(new Date(cropField.plantDate)) : false;
  const harvestDateInPast = cropField.harvestDate ? dateService.isInThePast(new Date(cropField.harvestDate)) : false;
  return !plantDateInFuture && !harvestDateInPast;
};
